// =====================================================================
// Connexion et inscription
// =====================================================================
// L'inscription ne demande que le strict nécessaire : nom, sexe, village,
// téléphone, mot de passe. Les parents et grands-parents se déclarent
// ensuite, depuis l'arbre. Exiger quatre grands-parents à l'inscription
// ferait abandonner la moitié des gens — ou les ferait inventer, ce qui
// est pire : des données inventées empoisonnent le rapprochement.
// =====================================================================

import { useState } from 'react';
import { api, ErreurApi } from '../api';
import type { Identite } from '../../partage/types';
import {
  Alerte,
  Champ,
  FormulaireIdentite,
  IconeCadenas,
  IconeEnveloppe,
  IconeMarque,
  identiteValide,
  useEnLigne,
} from '../composants/base';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function Connexion({
  onConnecte,
  onOublie,
}: {
  onConnecte: () => void;
  onOublie: () => void;
}) {
  const [mode, setMode] = useState<'connexion' | 'inscription'>('connexion');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [identite, setIdentite] = useState<Identite>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const enLigne = useEnLigne();
  /** Non nul quand on attend le code : porte l'adresse à confirmer. */
  const [aVerifier, setAVerifier] = useState<string | null>(null);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      if (mode === 'connexion') {
        await api.connexion(email, motDePasse);
      } else {
        const r = await api.inscription({ identite, email, mot_de_passe: motDePasse });
        // Sans transport de courriel configuré, le serveur ouvre la
        // session tout de suite : il n'y a pas de code à attendre.
        if (r.verification_requise) {
          setAVerifier(r.email ?? email);
          return;
        }
      }
      onConnecte();
    } catch (e) {
      // Une adresse non confirmée n'est pas une erreur de saisie : le
      // serveur vient de renvoyer un code, on passe à l'écran qui
      // l'attend.
      if (e instanceof ErreurApi && e.statut === 403 && e.details?.verification_requise) {
        setAVerifier(String(e.details.email ?? email));
        return;
      }
      setErreur(
        e instanceof ErreurApi ? e.message : 'Une erreur est survenue.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  if (aVerifier) {
    return (
      <SaisieCode
        email={aVerifier}
        onVerifie={onConnecte}
        onRetour={() => {
          setAVerifier(null);
          setMode('connexion');
        }}
      />
    );
  }

  const inscription = mode === 'inscription';
  const pret =
    EMAIL.test(email.trim()) &&
    motDePasse.length >= (inscription ? 8 : 1) &&
    (!inscription || (identiteValide(identite) && Boolean(identite.nom?.trim())));

  return (
    <div className="contenu" style={{ paddingTop: 28 }}>
      <div className="titre-connexion">
        <IconeMarque className="marque-petite" />
        <div>
          <h1>Yambi</h1>
          <p className="doux">{"Ensemble, écrivons l'histoire de notre famille"}</p>
        </div>
      </div>

      <form className="carte" onSubmit={soumettre} noValidate>
        <h2>{inscription ? 'Créer mon compte' : 'Se connecter'}</h2>
        <p className="doux" style={{ marginBottom: 16 }}>
          {inscription
            ? 'Vos informations, pour commencer. Vous ajouterez votre famille ensuite.'
            : 'Entrez l’adresse avec laquelle vous vous êtes inscrit.'}
        </p>

        {!enLigne && (
          <Alerte>
            Vous êtes hors ligne. La connexion a besoin d'internet : une fois
            connecté, vous pourrez consulter votre arbre sans réseau.
          </Alerte>
        )}
        {erreur && <Alerte>{erreur}</Alerte>}

        {inscription && (
          <FormulaireIdentite valeur={identite} onChange={setIdentite} />
        )}

        <Champ id="email" etiquette="Adresse e-mail" icone={<IconeEnveloppe />}>
          <input
            id="email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.com"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
          />
        </Champ>

        <Champ
          id="mdp"
          etiquette="Mot de passe"
          icone={<IconeCadenas />}
          aide={inscription ? '8 caractères au minimum.' : undefined}
        >
          <input
            id="mdp"
            type="password"
            placeholder="votre mot de passe"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete={inscription ? 'new-password' : 'current-password'}
            minLength={inscription ? 8 : undefined}
            required
          />
        </Champ>

        <button className="bouton" type="submit" disabled={!pret || envoi || !enLigne}>
          {envoi
            ? 'Un instant…'
            : inscription
              ? 'Créer mon compte'
              : 'Se connecter'}
        </button>

        <div className="separateur" role="presentation">
          <span>ou</span>
        </div>

        <button
          type="button"
          className="bouton secondaire"
          onClick={() => {
            setMode(inscription ? 'connexion' : 'inscription');
            setErreur(null);
          }}
        >
          {inscription ? "J'ai déjà un compte" : 'Je veux créer un compte'}
        </button>

        {!inscription && (
          <p style={{ textAlign: 'center', marginTop: 14, marginBottom: 0 }}>
            <button type="button" className="bouton discret" onClick={onOublie}>
              J'ai oublié mon mot de passe
            </button>
          </p>
        )}
      </form>

      <p className="doux" style={{ textAlign: 'center', marginTop: 20 }}>
        Il n'y a aucune recherche dans Yambi. Personne ne peut vous trouver :
        seules les personnes avec qui vous avez un lien établi vous voient.
      </p>
    </div>
  );
}

/**
 * L'écran qui attend le code à six chiffres.
 *
 * Il valide ET connecte : celui qui vient de prouver qu'il relève cette
 * adresse n'a aucune raison de retaper son mot de passe juste après.
 *
 * Le champ est en `inputMode="numeric"` avec `autoComplete="one-time-code"`
 * — sur un téléphone, le clavier s'ouvre sur les chiffres et le système
 * propose le code dès qu'il le voit arriver, sans aller le chercher
 * dans l'autre application.
 */
function SaisieCode({
  email,
  onVerifie,
  onRetour,
}: {
  email: string;
  onVerifie: () => void;
  onRetour: () => void;
}) {
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function valider(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await api.verifierCode(email, code);
      onVerifie();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Code invalide.');
    } finally {
      setEnvoi(false);
    }
  }

  async function renvoyer() {
    setErreur(null);
    setMessage(null);
    try {
      await api.renvoyerVerification(email);
      setMessage('Un nouveau code vient de partir.');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Envoi impossible.');
    }
  }

  return (
    <div className="contenu" style={{ paddingTop: 28 }}>
      <div className="titre-connexion">
        <IconeMarque className="marque-petite" />
        <div>
          <h1>Yambi</h1>
          <p className="doux">{"Ensemble, écrivons l'histoire de notre famille"}</p>
        </div>
      </div>

      <form className="carte" onSubmit={valider} noValidate>
        <h2>Votre code</h2>
        <p className="doux" style={{ marginBottom: 16 }}>
          Nous avons envoyé six chiffres à <strong>{email}</strong>. Saisissez-les
          pour activer votre compte. Le code est valable 30 minutes.
        </p>

        {erreur && <Alerte>{erreur}</Alerte>}
        {message && <Alerte type="succes">{message}</Alerte>}

        <div className="champ">
          <label htmlFor="code">Code à six chiffres</label>
          <input
            id="code"
            className="champ-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            maxLength={6}
          />
        </div>

        <button className="bouton" type="submit" disabled={code.length !== 6 || envoi}>
          {envoi ? 'Vérification…' : 'Activer mon compte'}
        </button>

        <div className="separateur">
          <span>ou</span>
        </div>

        <button type="button" className="bouton secondaire" onClick={renvoyer}>
          Renvoyer le code
        </button>
        <button
          type="button"
          className="bouton discret"
          style={{ marginTop: 10, width: '100%' }}
          onClick={onRetour}
        >
          Revenir à la connexion
        </button>
      </form>

      <p className="doux" style={{ textAlign: 'center', marginTop: 16 }}>
        Le code n'arrive pas ? Regardez dans vos courriers indésirables.
      </p>
    </div>
  );
}
